import { useCallback, useEffect, useState } from "react";
import { usePlaidLink } from "react-plaid-link";
import { Loader2 } from "lucide-react";
import { api, errorText } from "@/lib/api";
import { PrimaryButton, SecondaryButton, SectionTitle } from "@/components/shared/ui";
import { ErrorBox } from "@/components/applicant/stageUi";

const Consent = ({ months, gambling }) => (
  <div className="flex flex-col gap-3 text-sm leading-relaxed" data-testid="bank-consent">
    <SectionTitle>Before you connect</SectionTitle>
    <p><span className="font-medium">What we read:</span> the account your pay goes into and your savings accounts, and their transactions for the last {months} months.</p>
    <p><span className="font-medium">What we keep:</span> only money in that counts as income: the date, amount and payer name.</p>
    <p><span className="font-medium">What we throw away:</span> everything else, including your spending, balances and account numbers. Credit cards and loans are never read.</p>
    {gambling && (
      <p data-testid="bank-consent-gambling"><span className="font-medium">For this property:</span> we also count gambling transactions and missed-payment or overdraft fees. Only the totals are shared, never the merchants.</p>
    )}
    <p className="text-muted-foreground">The connection is removed as soon as the check finishes.</p>
  </div>
);

export const LinkLauncher = ({ token, onSuccess, onExit }) => {
  const { open, ready } = usePlaidLink({ token, onSuccess, onExit });
  useEffect(() => {
    if (ready) open();
  }, [ready, open]);
  return <p className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="size-4 animate-spin" /> Opening your bank…</p>;
};

export const BankConnect = ({ token, months, gambling, onUpdated, onCancel }) => {
  const [linkToken, setLinkToken] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const start = async () => {
    setBusy(true);
    setError("");
    try {
      setLinkToken((await api.post(`/a/${token}/bank/link-token`)).data.link_token);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  const onSuccess = useCallback(async (publicToken) => {
    setLinkToken(null);
    try {
      await api.post(`/a/${token}/bank/exchange`, { public_token: publicToken });
      await onUpdated();
      onCancel();
    } catch (err) {
      setError(errorText(err));
    }
  }, [token, onUpdated, onCancel]);

  return (
    <div className="mt-4 flex flex-col gap-4 border-t pt-4" data-testid="bank-connect">
      <Consent months={months} gambling={gambling} />
      {error && <ErrorBox testid="bank-connect-error">{error}</ErrorBox>}
      {linkToken ? <LinkLauncher token={linkToken} onSuccess={onSuccess} onExit={() => setLinkToken(null)} /> : (
        <div className="flex flex-col gap-2">
          <PrimaryButton size="lg" onClick={start} isLoading={busy} testid="bank-consent-continue">I agree, connect</PrimaryButton>
          <SecondaryButton size="lg" onClick={onCancel} testid="bank-consent-cancel">Back</SecondaryButton>
        </div>
      )}
    </div>
  );
};
